import React, { useState } from "react";
import { X, Send, CalendarIcon, Check, ArrowRight, ArrowLeft, Lock, Wallet as WalletIcon, Plus, TriangleAlert, CreditCard } from "lucide-react";
import { format } from "date-fns";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const STEPS = ["Details", "Build Package", "Review & Pay"];

export default function ServiceOnboardingModal({ open, onClose, service, onDone }) {
  const { profile, wallet, refreshWallet } = useApp();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [pkgId, setPkgId] = useState(null);
  const [addonIds, setAddonIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addAmt, setAddAmt] = useState(0);
  const [adding, setAdding] = useState(false);

  React.useEffect(() => {
    if (open) { setStep(0); setData({}); setPkgId(service?.packages?.[0]?.id || null); setAddonIds([]); }
  }, [open, service]);

  const fields = service?.intake_fields || [];
  const packages = service?.packages || [];
  const addons = service?.addons || [];
  const pkg = packages.find((p) => p.id === pkgId);
  const chosenAddons = addons.filter((a) => addonIds.includes(a.id));
  const total = (pkg?.price || 0) + chosenAddons.reduce((s, a) => s + a.price, 0);
  const balance = wallet?.balance ?? 0;
  const insufficient = balance < total;
  const shortfall = Math.max(0, total - balance);

  React.useEffect(() => {
    if (step === 2) setAddAmt(shortfall > 0 ? shortfall : total);
  }, [step, shortfall, total]);

  if (!open || !service) return null;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleAddon = (id) => setAddonIds((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const handleAddFunds = async () => {
    const amt = Number(addAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setAdding(true);
    try {
      await api.addFunds(profile.id, amt);
      await refreshWallet();
      toast.success(`$${amt.toLocaleString()} added to your wallet`);
    } catch {
      toast.error("Could not add funds.");
    } finally {
      setAdding(false);
    }
  };

  const validateDetails = () => {
    const missing = fields.filter((f) => f.required && !data[f.name]);
    if (missing.length) { toast.error(`Please fill: ${missing.map((m) => m.label).join(", ")}`); return false; }
    return true;
  };

  const next = () => {
    if (step === 0 && !validateDetails()) return;
    if (step === 1 && !pkgId) { toast.error("Please choose a package."); return; }
    setStep((s) => Math.min(s + 1, 2));
  };

  const purchase = async () => {
    setLoading(true);
    try {
      // "Just pay": if short, top up the exact shortfall first, then purchase.
      if (insufficient && shortfall > 0) {
        await api.addFunds(profile.id, shortfall);
        await refreshWallet();
      }
      await api.purchaseService(service.slug, profile.id, data, pkgId, addonIds);
      await refreshWallet();
      toast.success(`${service.title} purchased! Your project is now in progress.`);
      onClose();
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete purchase.");
    } finally {
      setLoading(false);
    }
  };

  const renderField = (f) => {
    if (f.type === "select") {
      return (
        <Select value={data[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
          <SelectTrigger className="w-full px-5 py-6 rounded-xl dark-input border-white/10 text-white"><SelectValue placeholder="Select an option" /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10 text-white max-h-64 z-[300]">{f.options.map((o) => (<SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>))}</SelectContent>
        </Select>
      );
    }
    if (f.type === "textarea") {
      return <textarea value={data[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} rows={3} className="w-full px-5 py-3.5 rounded-xl dark-input resize-none" placeholder={f.placeholder || ""} />;
    }
    if (f.type === "date") {
      const val = data[f.name] ? new Date(data[f.name]) : null;
      return (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="w-full px-5 py-3.5 rounded-xl dark-input flex items-center justify-between text-left">
              <span className={val ? "text-white" : "text-zinc-500"}>{val ? format(val, "PPP") : "Pick a date"}</span>
              <CalendarIcon size={16} className="text-zinc-500" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-zinc-900 border-white/10 z-[300]" align="start">
            <Calendar mode="single" selected={val} onSelect={(d) => d && set(f.name, d.toISOString())} className="text-white" />
          </PopoverContent>
        </Popover>
      );
    }
    return <input type="text" value={data[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className="w-full px-5 py-3.5 rounded-xl dark-input" placeholder={f.placeholder || ""} />;
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto">
      <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-2xl relative fade-in overflow-hidden border border-white/10 my-8">
        <div className="px-6 py-5 border-b border-white/10 flex justify-between items-center bg-white/5">
          <div>
            <h3 className="text-lg font-bold text-white tracking-tight">{service.title}</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Step {step + 1} of 3 · {STEPS[step]}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X /></button>
        </div>

        {/* Progress */}
        <div className="flex items-center px-6 py-4 border-b border-white/5 bg-black/20">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${i < step ? "bg-green-500/20 border-green-500 text-green-400" : i === step ? "bg-primary-600 border-primary-400 text-white" : "bg-white/5 border-white/10 text-zinc-500"}`}>{i < step ? <Check size={13} /> : i + 1}</div>
                <span className={`text-xs font-medium ${i === step ? "text-white" : "text-zinc-500"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-3 ${i < step ? "bg-green-500/40" : "bg-white/10"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="p-6 md:p-8 max-h-[60vh] overflow-y-auto hide-scrollbar">
          {step === 0 && (
            <div className="space-y-5">
              <p className="text-sm text-zinc-400">Tell us about your project so we can tailor everything to your needs.</p>
              {fields.map((f) => (
                <div key={f.name}>
                  <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">{f.label} {f.required && <span className="text-primary-400">*</span>}</label>
                  {renderField(f)}
                </div>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Choose a package</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {packages.map((p) => (
                    <button key={p.id} onClick={() => setPkgId(p.id)} className={`text-left p-4 rounded-xl border transition-all ${pkgId === p.id ? "border-primary-500 bg-primary-600/10 shadow-[0_0_15px_rgba(59,130,246,0.15)]" : "border-white/10 bg-black/30 hover:border-white/20"}`}>
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-bold text-white">{p.name}</span>
                        <span className="text-lg font-extrabold text-white">${p.price.toLocaleString()}</span>
                      </div>
                      <p className="text-xs text-zinc-400 mb-3">{p.desc}</p>
                      <ul className="space-y-1">
                        {p.features.map((ft) => (<li key={ft} className="text-[11px] text-zinc-400 flex items-center gap-1.5"><Check size={11} className="text-green-400 flex-shrink-0" /> {ft}</li>))}
                      </ul>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Add-ons <span className="text-zinc-500 normal-case font-normal">(optional — build your price)</span></h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {addons.map((a) => {
                    const on = addonIds.includes(a.id);
                    return (
                      <button key={a.id} onClick={() => toggleAddon(a.id)} className={`text-left p-4 rounded-xl border transition-all flex items-start gap-3 ${on ? "border-primary-500 bg-primary-600/10" : "border-white/10 bg-black/30 hover:border-white/20"}`}>
                        <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 mt-0.5 ${on ? "bg-primary-600 border-primary-500" : "border-white/20"}`}>{on && <Check size={13} className="text-white" />}</div>
                        <div className="flex-1">
                          <div className="flex justify-between"><span className="font-bold text-white text-sm">{a.name}</span><span className="text-sm font-bold text-white">+${a.price}</span></div>
                          <p className="text-[11px] text-zinc-400 mt-0.5">{a.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="bg-black/30 border border-white/5 rounded-2xl p-5">
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Order Summary</h4>
                <div className="flex justify-between text-sm mb-2"><span className="text-zinc-300">{pkg?.name}</span><span className="text-white font-bold">${pkg?.price.toLocaleString()}</span></div>
                {chosenAddons.map((a) => (<div key={a.id} className="flex justify-between text-sm mb-2 text-zinc-400"><span>+ {a.name}</span><span>${a.price}</span></div>))}
                <div className="border-t border-white/10 mt-3 pt-3 flex justify-between items-center"><span className="text-zinc-400 font-bold text-xs uppercase tracking-wider">Total Due Today</span><span className="text-2xl font-extrabold text-white">${total.toLocaleString()}</span></div>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-black/40 border border-white/5">
                <span className="text-sm text-zinc-400 flex items-center"><WalletIcon size={15} className="mr-2 text-green-400" /> Wallet Balance</span>
                <span className={`font-bold ${insufficient ? "text-orange-400" : "text-green-400"}`}>${balance.toLocaleString()}</span>
              </div>
              {insufficient ? (
                <div className="p-5 rounded-2xl bg-black/40 border border-orange-500/20 space-y-4">
                  <div className="flex items-center gap-2 text-orange-400 text-sm font-bold"><TriangleAlert size={16} /> Not enough in your wallet</div>
                  <p className="text-xs text-zinc-400 leading-relaxed">You're ${shortfall.toLocaleString()} short. Top up your wallet below, or tap <span className="text-white font-bold">Add ${shortfall.toLocaleString()} &amp; Pay</span> and we'll add exactly what's needed and complete your order.</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[shortfall, total, 1000, 5000].map((q, i) => (
                      <button key={i} type="button" onClick={() => setAddAmt(q)} className={`py-2 rounded-lg text-xs font-bold border transition-colors ${Number(addAmt) === q ? "bg-primary-600 text-white border-primary-500" : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"}`}>${q.toLocaleString()}</button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                      <input type="number" value={addAmt} onChange={(e) => setAddAmt(e.target.value)} className="w-full pl-8 pr-4 py-3 rounded-xl dark-input text-sm" />
                    </div>
                    <button type="button" onClick={handleAddFunds} disabled={adding} className="px-5 py-3 bg-white text-black rounded-xl text-sm font-bold hover:bg-zinc-200 transition disabled:opacity-60 flex items-center"><Plus size={15} className="mr-1.5" /> {adding ? "Adding…" : "Add Funds"}</button>
                  </div>
                  <p className="text-[11px] text-zinc-500 flex items-center"><CreditCard size={12} className="mr-1.5" /> Charged to {wallet?.payment_method || "•••• 4242"} · Demo mode, no real charges.</p>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-sm text-green-300 flex items-center"><Check size={15} className="mr-2" /> Your wallet has enough to cover this order. You're ready to pay.</div>
              )}
              <p className="text-[11px] text-zinc-600 text-center">Demo mode — funds are managed via your DONDRÁE Wallet; no real charges are made.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-white/10 bg-black/20 flex items-center justify-between">
          <div className="text-sm">
            <span className="text-zinc-500">Total: </span>
            <span className="text-white font-bold text-lg">${total.toLocaleString()}</span>
          </div>
          <div className="flex gap-3">
            {step > 0 && <button onClick={() => setStep((s) => s - 1)} className="px-4 py-2.5 border border-white/10 text-white rounded-xl font-bold hover:bg-white/5 transition-colors text-sm flex items-center"><ArrowLeft size={15} className="mr-1.5" /> Back</button>}
            {step < 2 ? (
              <button onClick={next} className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-500 transition-colors shadow-[0_0_15px_rgba(59,130,246,0.4)] text-sm flex items-center">Continue <ArrowRight size={15} className="ml-1.5" /></button>
            ) : (
              <button onClick={purchase} disabled={loading} className="px-6 py-2.5 bg-green-600 text-white rounded-xl font-bold hover:bg-green-500 transition-colors text-sm flex items-center disabled:opacity-50">
                {loading ? "Processing…" : insufficient ? (<>Add ${shortfall.toLocaleString()} &amp; Pay <Lock size={14} className="ml-2" /></>) : (<>Confirm &amp; Pay <Lock size={14} className="ml-2" /></>)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
